// CraftingView — craft template cards behind an ICON tab bar named by SKILL
// (fixed order: crafting, smithing, cooking, leatherworking,
// tailoring, fletching, moulding; tooltips name the skill). A skill-xp header
// under the tabs shows the page's skill level + progress to next level
// (infos skill_<id>). Cards group into COLLAPSIBLE sections (server `group`
// field; fold state remembered per session). The guided skill's tab GLOWS
// while not on screen — never auto-selects. Opening the view (or switching
// skill tabs) closes any bench left up. Cards sort by the server `order`
// field (level/key fallback). The bench is a bottom sheet.

import { clear, el, kbar, tooltip } from '../kit';
import { sendEvent } from '../net';
import * as icons from '../icons';
import * as ui from '../ui';
import * as vsj from '../vsj';
import { activityCard, CardHandle } from '../components/activity_card';
import { benchSheet } from '../components/bench_sheet';

// tab ORDER is served (vsj.craft_tabs); unknown skills append after,
// alphabetically. Only the tab art below is client presentation.
// existing item/glyph art stands in for each skill (art pipeline keys)
const SKILL_ICONS: Record<string, string> = {
  crafting: 'stone_handaxe', smithing: 'anvil_kit_iron',
  cooking: 'trout_cooked', leatherworking: 'light_leather', tailoring: 'linen_bolt',
  fletching: 'arrow', moulding: 'clay',
};
const capWord = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);
const skillRank = (s: string) => {
  const order: any[] = vsj.at('craft_tabs') ?? [];
  const i = order.indexOf(s);
  return i === -1 ? order.length : i;
};

export function craftingView(): {
  root: HTMLElement;
  refresh(changed: Set<string>): void;
  onShown(): void;
} {
  const catBar = el('div', { class: 'craft-tabs' });
  // skill-xp header for the page's crafting skill: name · Lv N + progress bar
  const skillName = el('span', { class: 'k-small', style: 'color:var(--c-skill);flex:none' });
  const skillXpBar = kbar('xp', 6);
  skillXpBar.root.style.flex = '1';
  skillXpBar.root.style.alignSelf = 'center';
  const skillHead = el('div', { style: 'display:none;align-items:center;gap:8px' }, skillName, skillXpBar.root);
  tooltip(skillHead, () => {
    const info = currentCat ? vsj.info('skill_' + currentCat) : null;
    const toNext = Number(info?.xp_to_next ?? 0);
    return toNext > 0 ? `${toNext} xp to Lv ${Number(info?.level ?? 0) + 1}` : 'Top level';
  });
  const emptyLabel = el('div', {
    class: 'k-faint',
    style: 'display:none',
    text: 'Nothing to craft yet — gather materials and follow your quest bar.',
  });
  const groupsBox = el('div', { style: 'display:flex;flex-direction:column;gap:10px' });
  const scroll = el('div', { class: 'k-scroll', style: 'flex:1;min-height:0' }, emptyLabel, groupsBox);
  const bench = benchSheet();
  const root = el(
    'div',
    { class: 'k-panel', style: 'flex:1;min-height:0;display:flex;flex-direction:column;gap:6px;padding:8px' },
    catBar,
    skillHead,
    scroll,
    bench.root,
  );

  let catSig = '';
  let currentCat = '';
  const catButtons: Record<string, HTMLButtonElement> = {};
  const cards: Record<string, CardHandle> = {};

  const closeBenchIfOpen = () => {
    const b = vsj.at('bench');
    if (b && typeof b === 'object') sendEvent('close_bench');
  };

  const tabOf = (a: any): string => String(a.skill ?? 'other');
  const groupOf = (a: any): string => String(a.group ?? 'Crafts');
  const foldKey = (cat: string, group: string) => `craftfold:${cat}:${group}`;
  const isFolded = (cat: string, group: string): boolean => {
    try { return sessionStorage.getItem(foldKey(cat, group)) === '1'; } catch { return false; }
  };
  const setFolded = (cat: string, group: string, folded: boolean) => {
    try {
      if (folded) sessionStorage.setItem(foldKey(cat, group), '1');
      else sessionStorage.removeItem(foldKey(cat, group));
    } catch { /* session-only nicety */ }
  };

  const refreshSkillHead = () => {
    const info = currentCat ? vsj.info('skill_' + currentCat) : null;
    skillHead.style.display = info ? 'flex' : 'none';
    if (!info) return;
    skillName.textContent = `${info.name ?? capWord(currentCat)} · Lv ${info.level ?? '?'}`;
    const into = Number(info.xp_into_level ?? 0);
    const toNext = Number(info.xp_to_next ?? 0);
    skillXpBar.set(toNext <= 0 ? 1 : into / Math.max(1, into + toNext));
  };

  const refresh = () => {
    const acts = vsj.at('activities') ?? {};
    const glow: string[] = vsj.at('guidance.glow') ?? [];

    const wanted: Record<string, any> = {};
    for (const key of Object.keys(acts)) {
      const a = acts[key];
      if (a && typeof a === 'object' && String(a.kind ?? '') === 'craft') wanted[key] = a;
    }
    emptyLabel.style.display = Object.keys(wanted).length ? 'none' : '';
    catBar.style.display = Object.keys(wanted).length ? '' : 'none';

    for (const key of Object.keys(cards))
      if (!wanted[key]) {
        cards[key].root.remove();
        delete cards[key];
      }

    const catNames = [...new Set(Object.values(wanted).map(tabOf))].sort(
      (a, b) => skillRank(a) - skillRank(b) || (a < b ? -1 : 1),
    );

    const cs = catNames.join(',');
    if (cs !== catSig) {
      catSig = cs;
      clear(catBar);
      for (const k of Object.keys(catButtons)) delete catButtons[k];
      for (const cat of catNames) {
        // icon tab: art stands in for the word; tooltip still names the skill
        const b = el('button', { class: 'k-btn compact craft-tab' });
        b.append(icons.texture(SKILL_ICONS[cat] ?? 'rock', 22));
        tooltip(b, () => capWord(cat));
        b.addEventListener('click', () => {
          currentCat = cat;
          closeBenchIfOpen();
          refresh();
        });
        catBar.append(b);
        catButtons[cat] = b;
      }
    }

    let glowCat = '';
    for (const key of Object.keys(wanted))
      if (glow.includes('card:' + key)) {
        glowCat = tabOf(wanted[key]);
        break;
      }
    if (!currentCat || !catNames.includes(currentCat)) currentCat = catNames[0] ?? '';
    for (const cat of Object.keys(catButtons)) {
      catButtons[cat].classList.toggle('active', cat === currentCat);
      catButtons[cat].classList.toggle('glow', cat === glowCat && cat !== currentCat);
    }
    refreshSkillHead();

    // server-curated order within the page (recipes.ts: processing ->
    // intermediates -> finished); level then key as fallback
    const keys = Object.keys(wanted).sort((a, b) => {
      const oa = Number(wanted[a].order ?? 1 << 30), ob = Number(wanted[b].order ?? 1 << 30);
      if (oa !== ob) return oa - ob;
      const la = Number(wanted[a].level ?? 0), lb = Number(wanted[b].level ?? 0);
      if (la !== lb) return la - lb;
      return a < b ? -1 : 1;
    });

    // current page's cards, bucketed into collapsible sections by the served
    // `group` heading; section order = first card's order within the page
    const pageKeys = keys.filter((k) => tabOf(wanted[k]) === currentCat);
    const groupNames: string[] = [];
    const byGroup: Record<string, string[]> = {};
    for (const k of pageKeys) {
      const g = groupOf(wanted[k]);
      if (!byGroup[g]) {
        byGroup[g] = [];
        groupNames.push(g);
      }
      byGroup[g].push(k);
    }
    clear(groupsBox); // headers rebuild; card handles persist across refreshes
    const oneGroup = groupNames.length <= 1;
    for (const g of groupNames) {
      const folded = !oneGroup && isFolded(currentCat, g);
      const flow = el('div', { class: 'card-flow' });
      if (folded) flow.style.display = 'none';
      const section = el('div', { style: 'display:flex;flex-direction:column;gap:6px' });
      if (!oneGroup) {
        // fold header: caret + heading + card count; click toggles
        const head = el(
          'div',
          { class: 'craft-fold k-small' },
          el('span', { class: 'k-dim', text: folded ? '▸' : '▾' }),
          el('span', { text: g }),
          el('span', { class: 'k-faint', text: `(${byGroup[g].length})` }),
        );
        head.addEventListener('click', () => {
          setFolded(currentCat, g, flow.style.display !== 'none');
          refresh();
        });
        section.append(head);
      }
      section.append(flow);
      groupsBox.append(section);
      for (const key of byGroup[g]) {
        let card = cards[key];
        if (!card) {
          card = activityCard(false);
          cards[key] = card;
        }
        flow.append(card.root);
        card.update(key, wanted[key], glow);
      }
    }
    // off-page cards still update their data (cheap; keeps handles warm)
    for (const key of keys) {
      if (tabOf(wanted[key]) === currentCat) continue;
      let card = cards[key];
      if (!card) {
        card = activityCard(false);
        cards[key] = card;
      }
      card.update(key, wanted[key], glow);
    }
  };

  refresh();
  return {
    root,
    refresh(changed) {
      if (changed.has('activities') || changed.has('guidance') || changed.has('infos') || changed.has('*'))
        refresh();
      bench.refresh(changed);
    },
    onShown() {
      // "take me there": a jump landed pointing at one template — hop to its
      // skill tab (user-driven, not guidance) and flash the card. The jump
      // already sent select_card, so DON'T close the bench it just opened.
      const key = ui.consumeFlash('crafting');
      if (!key) {
        closeBenchIfOpen(); // first sight of the page is the LIST
        return;
      }
      const acts = vsj.at('activities') ?? {};
      const a = acts[key];
      if (a && typeof a === 'object') {
        currentCat = tabOf(a);
        refresh();
      }
      if (cards[key]) ui.flashTarget(cards[key].root);
    },
  };
}
